import { useEffect } from 'react';

/**
 * Caret-preserving contentEditable sync, extracted from `TextEditor.js`'s `RichHtmlEditor` so
 * `IdmlPageSpreadEditor.js`'s target frames (design doc §7) can reuse the same pattern instead of
 * duplicating it ad hoc.
 *
 * A plain `<div contentEditable>` whose `innerHTML` is reset from external state on every render
 * loses the caret position (it snaps back to the start) the moment the user types, because
 * setting `innerHTML` always recreates the DOM subtree. The fix used here: only push `value` into
 * the DOM when the element does *not* currently hold focus (or contain the focused/active
 * element) -- i.e. when the change came from somewhere other than the user's own typing in this
 * element. While the user is actively editing, the DOM is left alone and `onInput` is the only
 * thing driving `value` forward.
 *
 * @param {React.RefObject<HTMLElement>} editorRef ref to the contentEditable element
 * @param {string} value the external HTML string to keep the element in sync with
 */
export function useCaretPreservingHtmlSync(editorRef, value) {
    useEffect(() => {
        if (
            editorRef.current &&
            editorRef.current.innerHTML !== (value || '') &&
            !editorRef.current.contains(document.activeElement)
        ) {
            editorRef.current.innerHTML = value || '';
        }
    }, [value, editorRef]);
}

/** onInput handler to wire onto the contentEditable element alongside the hook above. */
export const handleCaretPreservingInput = (event, onChange) => onChange(event.currentTarget.innerHTML);
